import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { List, Input, Empty, Dropdown, Menu, Button, Tag, Checkbox } from "antd";
import { EllipsisOutlined } from "@ant-design/icons";
const { Search } = Input;
/** 点击是否来自行内可交互控件（复选框、按钮等），若是则不应触发行 onSelect */
function isClickFromRowInteractiveControl(target) {
    if (target == null || !(target instanceof Element))
        return false;
    return Boolean(target.closest([
        "input",
        "textarea",
        "select",
        "button",
        'a[href]',
        '[role="checkbox"]',
        '[role="switch"]',
        ".ant-checkbox",
        ".ant-checkbox-wrapper",
        ".ant-checkbox-input",
        ".ant-switch",
        ".ant-radio",
        ".ant-radio-wrapper",
        ".list-item-menu-button",
    ].join(",")));
}
const extractNumberFromContent = (content) => {
    if (typeof content === "number")
        return content.toString();
    if (typeof content === "string")
        return content;
    if (React.isValidElement(content)) {
        const children = content.props?.children;
        if (children) {
            if (typeof children === "string") {
                const match = children.match(/(\d+)/);
                return match ? match[1] : "0";
            }
            if (typeof children === "number") {
                return children.toString();
            }
        }
    }
    return "0";
};
const CompsList = ({ listData = [], loading = false, onSelect, showSearch = true, selectedId, rowSelection, menuItems = [], onMenuClick, height = "100%", style, className, searchPlaceholder = "搜索...", defaultSearchValue = "", draggable = false, onDragStart, onReorder, itemRowAlignItems = "center", }) => {
    const [searchValue, setSearchValue] = useState(defaultSearchValue);
    const [draggingId, setDraggingId] = useState(null);
    const filteredListData = useMemo(() => {
        if (!searchValue)
            return listData;
        const lower = searchValue.toLowerCase();
        return listData.filter((item) => {
            const raw = typeof item.searchText === "string"
                ? item.searchText
                : typeof item.leftContent === "string"
                    ? item.leftContent
                    : "";
            return raw.toLowerCase().includes(lower);
        });
    }, [listData, searchValue]);
    /** 与行 id 比较时统一为字符串，避免 selectedKeys 为 string、item.id 为 number 时 includes 恒为 false */
    const selectedRowKeySet = useMemo(() => new Set((rowSelection?.selectedRowKeys ?? []).map((k) => String(k))), [rowSelection?.selectedRowKeys]);
    const filteredIdStrings = useMemo(() => filteredListData.map((i) => String(i.id)), [filteredListData]);
    const rowSelectionRef = useRef(rowSelection);
    rowSelectionRef.current = rowSelection;
    const applySelectAllFiltered = useCallback((checked) => {
        const rs = rowSelectionRef.current;
        if (!rs?.onChange)
            return;
        const currentKeys = rs.selectedRowKeys ?? [];
        if (checked) {
            const merged = new Set([
                ...currentKeys.map((k) => String(k)),
                ...filteredIdStrings,
            ]);
            rs.onChange([...merged]);
            return;
        }
        const filteredSet = new Set(filteredIdStrings);
        rs.onChange(currentKeys.filter((k) => !filteredSet.has(String(k))));
    }, [filteredIdStrings]);
    useEffect(() => {
        // 当上游 listData 变化时，保持当前搜索逻辑
    }, [listData]);
    const renderItemMenu = (item) => {
        if (!onMenuClick || !menuItems.length)
            return null;
        const menu = (_jsx(Menu, { onClick: ({ key }) => {
                onMenuClick && onMenuClick(item, key);
            }, children: menuItems.map((menuItem) => (_jsx(Menu.Item, { children: menuItem.label }, menuItem.key))) }));
        return (_jsx(Dropdown, { overlay: menu, trigger: ["click"], children: _jsx(Button, { type: "text", icon: _jsx(EllipsisOutlined, {}), size: "small", onClick: (e) => {
                    e.stopPropagation();
                }, style: {
                    marginLeft: "auto",
                    padding: "0 5px",
                    color: "#bfbfbf",
                }, className: "list-item-menu-button" }) }));
    };
    const fillFlexParent = height === "100%";
    return (_jsxs("div", { className: className, style: {
            height: typeof height === "number" ? `${height}px` : height,
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
            ...(fillFlexParent ? { flex: 1, overflow: "hidden" } : {}),
            ...style,
        }, children: [showSearch && (_jsx(Search, { placeholder: searchPlaceholder, allowClear: true, value: searchValue, onChange: (e) => setSearchValue(e.target.value), style: { marginBottom: 16 } })), rowSelection?.showSelectAll && filteredListData.length > 0 ? (_jsx("div", { style: {
                    marginBottom: 12,
                    display: "flex",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: 8,
                    flexShrink: 0,
                }, onMouseDown: (e) => {
                    e.stopPropagation();
                }, onClick: (e) => {
                    e.stopPropagation();
                }, children: (() => {
                    const n = filteredIdStrings.filter((id) => selectedRowKeySet.has(id)).length;
                    const allFilteredSelected = filteredIdStrings.length > 0 &&
                        filteredIdStrings.every((id) => selectedRowKeySet.has(id));
                    const someFilteredSelected = n > 0 && n < filteredIdStrings.length;
                    return (_jsxs(_Fragment, { children: [_jsx(Checkbox, { indeterminate: someFilteredSelected, checked: allFilteredSelected, onChange: (e) => {
                                    applySelectAllFiltered(e.target.checked);
                                }, children: rowSelection.selectAllLabel ?? "全选" }), someFilteredSelected ? (_jsxs("span", { style: { fontSize: 12, color: "rgba(0,0,0,0.45)" }, children: ["\u5DF2\u9009 ", n, " / ", filteredIdStrings.length] })) : null] }));
                })() })) : null, _jsx("div", { style: {
                    flex: 1,
                    minHeight: 0,
                    overflow: "auto",
                }, children: filteredListData.length > 0 ? (_jsx(List, { className: "comps-list", dataSource: filteredListData, 
                    /**
                     * 带 rowSelection 时：有行仍传 loading 会让 antd List 叠 Spin 层，常拦截行首复选点击。
                     * 无 rowSelection 的列表保持原 loading 语义（如刷新时整表转圈）。
                     */
                    loading: rowSelection ? loading && filteredListData.length === 0 : loading, renderItem: (item) => (_jsx(List.Item, { onClick: (e) => {
                            if (!onSelect)
                                return;
                            if (isClickFromRowInteractiveControl(e.target))
                                return;
                            onSelect(item);
                        }, draggable: draggable, onDragStart: (e) => {
                            if (draggable && onDragStart) {
                                setDraggingId(item.id);
                                onDragStart(item, e);
                            }
                            else if (draggable) {
                                // 默认处理：将数据存储到 dataTransfer
                                setDraggingId(item.id);
                                e.dataTransfer.setData("text/plain", item.id);
                                e.dataTransfer.effectAllowed = "move";
                            }
                        }, onDragOver: (e) => {
                            if (!draggable)
                                return;
                            // 仅在未开启搜索时允许拖拽排序，避免过滤列表导致索引错乱
                            if (searchValue)
                                return;
                            e.preventDefault();
                        }, onDrop: (e) => {
                            if (!draggable || !onReorder)
                                return;
                            if (searchValue)
                                return;
                            e.preventDefault();
                            const sourceId = draggingId || e.dataTransfer.getData("text/plain");
                            if (!sourceId || sourceId === item.id)
                                return;
                            const sourceIndex = listData.findIndex((it) => it.id === sourceId);
                            const targetIndex = listData.findIndex((it) => it.id === item.id);
                            if (sourceIndex === -1 || targetIndex === -1)
                                return;
                            const newList = [...listData];
                            const [moved] = newList.splice(sourceIndex, 1);
                            newList.splice(targetIndex, 0, moved);
                            onReorder(newList);
                            setDraggingId(null);
                        }, style: {
                            cursor: draggable ? "grab" : "default",
                            backgroundColor: selectedId === item.id ? "#f0f7ff" : "transparent",
                            padding: "8px",
                            borderRadius: "4px",
                            transition: "background-color 0.3s",
                        }, className: "comps-list-item", children: _jsxs("div", { style: {
                                display: "flex",
                                justifyContent: "space-between",
                                width: "100%",
                                alignItems: itemRowAlignItems,
                            }, children: [rowSelection ? (_jsx("span", { className: "comps-list-row-selection-wrap", style: {
                                        marginRight: 10,
                                        flexShrink: 0,
                                        display: "inline-flex",
                                        alignItems: itemRowAlignItems,
                                        position: "relative",
                                        zIndex: 1,
                                    }, onMouseDown: (e) => {
                                        /** 阻止冒泡到 List.Item / Spin，避免外层拦截指针事件 */
                                        e.stopPropagation();
                                    }, onClick: (e) => {
                                        e.stopPropagation();
                                    }, children: _jsx(Checkbox, { checked: selectedRowKeySet.has(String(item.id)), disabled: rowSelection.getCheckboxProps?.(item)?.disabled, onChange: () => {
                                            const rs = rowSelectionRef.current;
                                            if (!rs?.onChange)
                                                return;
                                            if (rs.getCheckboxProps?.(item)?.disabled)
                                                return;
                                            const rawKeys = rs.selectedRowKeys ?? [];
                                            const idStr = String(item.id);
                                            const wasOn = rawKeys.some((k) => String(k) === idStr);
                                            const next = wasOn
                                                ? rawKeys.filter((k) => String(k) !== idStr)
                                                : [...rawKeys, item.id];
                                            rs.onChange(next);
                                        } }) })) : null, _jsxs("div", { style: {
                                        display: "flex",
                                        alignItems: itemRowAlignItems,
                                        fontWeight: selectedId === item.id ? 500 : 400,
                                        justifyContent: "space-between",
                                        flex: 1,
                                        minWidth: 0,
                                        cursor: onSelect ? "pointer" : "default",
                                    }, onClick: onSelect
                                        ? () => {
                                            onSelect(item);
                                        }
                                        : undefined, role: onSelect ? "button" : undefined, children: [_jsx("div", { style: { marginRight: 8, minWidth: 0, flex: 1 }, children: item.leftContent }), item.rightContent && (_jsx(_Fragment, { children: React.isValidElement(item.rightContent) ? (item.rightContent) : (() => {
                                                const extractedNumber = extractNumberFromContent(item.rightContent);
                                                const isNumber = typeof item.rightContent === "number" ||
                                                    (typeof item.rightContent === "string" && /^\d+$/.test(item.rightContent)) ||
                                                    (extractedNumber !== "0" && extractedNumber !== "");
                                                if (isNumber) {
                                                    return (_jsx(Tag, { color: "blue", style: { marginInlineEnd: 0 }, children: extractedNumber }));
                                                }
                                                else {
                                                    return item.rightContent;
                                                }
                                            })() }))] }), renderItemMenu(item)] }) }, item.id)) })) : (_jsx(Empty, { description: "\u6682\u65E0\u6570\u636E" })) })] }));
};
export default CompsList;
